/**
 * What a period round canopy *is*, in numbers and buffers: its proportions, its
 * two silks, and the three geometries a rig is assembled from.
 *
 * Separated from `canopy-kit.ts` so that the question "what shape is a
 * parachute" has exactly one answer and one place, and the question "how are
 * canopies handed out and taken back" has another. Both halves are shared by
 * the player's canopy (`scene-parachute.ts`) and by AD-021b's ambience jumpers
 * (`scene-ambient-jumpers.ts`) — there is one parachute in this port, not two
 * modules that resemble each other.
 *
 * **A runtime mesh, not an enhanced-model asset.** The user asked for "nice old
 * round WW2 parachutes", and the cheap honest way to draw one here is the way
 * this scene already draws its sun, its sky, its propeller discs and its
 * ground: procedural three.js geometry built at boot (`celestial-disc.ts`,
 * `world.ts`, `propeller-discs.ts`). Putting it through `tools/blender` instead
 * would have meant a 114 MB regeneration, a Blender dependency, and a diff in
 * the enhanced generator for one hemisphere — and a parachute is not an
 * aeroplane, so nothing in the `.glb` fleet's LOD machinery has anything to
 * offer it.
 *
 * ## What makes it read as a period canopy rather than a dome
 *
 * Three things, in the order they matter:
 *
 * 1. **Gores.** A silk canopy is sewn from wedge-shaped panels, and the seams
 *    between them are the single most recognisable thing about the shape. They
 *    are drawn as alternating vertex-coloured wedges rather than as geometry:
 *    two tones a step apart make a seam without a single extra triangle.
 * 2. **A skirt that flares.** The profile is not a hemisphere — an inflated
 *    round canopy is fuller than half a sphere and turns outward at the hem, so
 *    the polar sweep runs past 90 degrees.
 * 3. **The vent.** Every canopy of the period has a hole at the apex, and the
 *    dark disc it makes against the sky is the second recognisable thing.
 *
 * ## Materials: flat, opaque, vertex-coloured
 *
 * The materials themselves are `canopy-kit.ts`'s, but the rule they obey is
 * this file's business: `MeshBasicNodeMaterial` with `vertexColors`, no
 * lighting model — because nothing else in this scene has one either
 * (`ramp-material.ts`) — and **nothing `transparent`**, which is the standing
 * rule this app's reversed-Z depth buffer imposes (`three/particle-cloud.ts`).
 * Silk is opaque anyway, so obeying it costs nothing. The underside is a second
 * shell in its own darker tone rather than a `DoubleSide` face, because with no
 * lighting model a two-sided face has exactly one colour and the underside is
 * the view a descending pilot's camera has for most of the way down.
 *
 * ## The rig's origin is the man's boots, and that is a correction
 *
 * It used to be the *canopy centre*: `scene-parachute.ts` set its group to
 * `ParachuteState.position` and hung the pilot capsule 7.35 m below it. But the
 * engine's position **is the man** — `beginJump` copies the aeroplane's own
 * position and `stepParachute` stops *that* point at `groundHeightM` — so on
 * the tick the engine reported `landed`, the drawn pilot was seven metres under
 * the drawn ground with the canopy resting on it. A rig is now anchored at his
 * boots and the silk lifted `RIG_LIFT_M` above them, which is the same rule
 * `scene-shadows.ts` and `scene-crash-marks.ts` are built on and the fault class
 * `lessons.md` records four times.
 */

import * as THREE from "three/webgpu";

/**
 * Canopy radius, metres. A 28-ft flat-circular canopy of the period inflates to
 * roughly 7 m across, so the *inflated* radius is a little under half the flat
 * one — 3.4 m here.
 */
export const CANOPY_RADIUS_M = 3.4;

/** How far past a hemisphere the profile sweeps, so the hem flares outward. */
export const CANOPY_SWEEP_RAD = Math.PI * 0.62;

/** The apex vent, as a fraction of the polar sweep. */
export const VENT_FRACTION = 0.08;

/** Panels around the canopy. 12 is the period figure and reads as gores at every distance this scene has. */
export const GORE_COUNT = 12;

export const CANOPY_RINGS = 8;

/** Rigging length from hem to harness, metres. */
export const LINE_LENGTH_M = 5.6;

/** The two silks, a step apart — bleached canvas and its own shadow. */
const GORE_LIGHT = new THREE.Color(0.86, 0.83, 0.74);
const GORE_DARK = new THREE.Color(0.66, 0.63, 0.55);
/** The underside is in its own shade all day, which is most of what sells the volume with no lighting model. */
const UNDERSIDE_SCALE = 0.72;

export const LINE_COLOUR = new THREE.Color(0.28, 0.26, 0.22);
export const PILOT_COLOUR = new THREE.Color(0.19, 0.17, 0.15);

/** The man in the harness: a capsule, radius and cylinder length in metres. */
export const PILOT_RADIUS_M = 0.26;
export const PILOT_LENGTH_M = 0.85;
/** Head to boots, which is what decides where his feet are. */
export const PILOT_HALF_HEIGHT_M = PILOT_LENGTH_M / 2 + PILOT_RADIUS_M;

/** Where the rigging gathers, in canopy-local metres: the hem, minus the lines. */
export const HARNESS_Y = CANOPY_RADIUS_M * Math.cos(CANOPY_SWEEP_RAD) - LINE_LENGTH_M;
/** He hangs a little below the gather, in the harness rather than at it. */
const PILOT_CENTRE_Y = HARNESS_Y - 0.5;

/**
 * How far the silk sits above the rig's origin.
 *
 * The origin is **the man's boots**, because that is the point the engine
 * integrates and stops on the ground (see the module note's first correction).
 * So everything the canopy-local geometry places below zero is lifted by
 * exactly the distance from his boots to the canopy centre.
 */
export const RIG_LIFT_M = -(PILOT_CENTRE_Y - PILOT_HALF_HEIGHT_M);


/**
 * Builds the canopy shell.
 *
 * Hand-built rather than `SphereGeometry` because the gores need a colour per
 * *panel* and a shared-vertex sphere cannot have one: the seam between two
 * panels is a colour discontinuity, so the two sides of it must be two
 * vertices. Every quad is therefore its own four vertices, which for 12 x 8
 * panels is 384 triangles — nothing, and it buys the one feature that makes the
 * shape recognisable.
 */
export function canopyGeometry(): THREE.BufferGeometry {
  const positions: number[] = [];
  const colours: number[] = [];
  const vent = CANOPY_SWEEP_RAD * VENT_FRACTION;

  const at = (ring: number, gore: number): THREE.Vector3 => {
    const phi = vent + ((CANOPY_SWEEP_RAD - vent) * ring) / CANOPY_RINGS;
    const theta = (gore / GORE_COUNT) * Math.PI * 2;
    return new THREE.Vector3(
      CANOPY_RADIUS_M * Math.sin(phi) * Math.cos(theta),
      CANOPY_RADIUS_M * Math.cos(phi),
      CANOPY_RADIUS_M * Math.sin(phi) * Math.sin(theta),
    );
  };

  for (let gore = 0; gore < GORE_COUNT; gore += 1) {
    const tone = gore % 2 === 0 ? GORE_LIGHT : GORE_DARK;
    for (let ring = 0; ring < CANOPY_RINGS; ring += 1) {
      const a = at(ring, gore);
      const b = at(ring, gore + 1);
      const c = at(ring + 1, gore + 1);
      const d = at(ring + 1, gore);
      // Two triangles, wound so the *outside* faces out. The material is
      // `DoubleSide`, so the underside draws too — and it draws darker, which
      // is done by colour rather than by a light.
      for (const corner of [a, b, c, a, c, d]) positions.push(corner.x, corner.y, corner.z);
      for (let i = 0; i < 6; i += 1) colours.push(tone.r, tone.g, tone.b);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colours, 3));
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * The underside, drawn as its own shell a hair inside the canopy in its own
 * darker tone.
 *
 * Two shells rather than one two-sided one, because with no lighting model a
 * `DoubleSide` face has exactly one colour and a canopy lit from above must not
 * look the same from underneath — which is the view the player has for most of
 * the descent, since the camera follows him down and the canopy is above him.
 */
export function undersideGeometry(): THREE.BufferGeometry {
  const geometry = canopyGeometry();
  const colours = geometry.getAttribute("color") as THREE.BufferAttribute;
  for (let i = 0; i < colours.count; i += 1) {
    colours.setXYZ(
      i,
      colours.getX(i) * UNDERSIDE_SCALE,
      colours.getY(i) * UNDERSIDE_SCALE,
      colours.getZ(i) * UNDERSIDE_SCALE,
    );
  }
  geometry.scale(0.985, 0.985, 0.985);
  return geometry;
}

/** The rigging: one line per gore seam, hem to harness. */
export function lineGeometry(): THREE.BufferGeometry {
  const points: number[] = [];
  const phi = CANOPY_SWEEP_RAD;
  for (let gore = 0; gore < GORE_COUNT; gore += 1) {
    const theta = (gore / GORE_COUNT) * Math.PI * 2;
    points.push(
      CANOPY_RADIUS_M * Math.sin(phi) * Math.cos(theta),
      CANOPY_RADIUS_M * Math.cos(phi),
      CANOPY_RADIUS_M * Math.sin(phi) * Math.sin(theta),
      0,
      HARNESS_Y,
      0,
    );
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(points, 3));
  return geometry;
}
